import React, { useState, useEffect, useCallback, useMemo } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';

import { getMyKvkkRequests, createMyKvkkRequest } from '../../services/kvkkService';
import DataTable from '../../components/ui/DataTable';
import GenericFormDrawer from '../../components/GenericFormDrawer';
import { dateFilterColumn } from '../../utils/dateFilterColumn';
import { formatDateTime } from '../../utils/Utils';
import { StatusBadge } from '../../components/kvkk/statusBadge';
import KvkkRequestDetailDrawer from './KvkkRequestDetailDrawer';

// KVKK Taleplerim — KVKK md. 11 başvurusu (silme / düzeltme / bilgi), spec §4.2.
// Çalışan başına birkaç satır: istemci tarafı tablo. Yeni kayıt GenericFormDrawer ile
// (modal YOK), düğme tablonun içinde, başlık yalnız DataTable'da. Satır tıklanınca salt
// okunur çekmecede tam açıklama ve tam İK notu (tabloda not kısaltılır).
//
// Durumlar: 'loading' | 'ready' | 'noEmployee' | 'unavailable'. Çalışan kaydı olmayan
// hesapta backend 404 `kvkk.request.employeeNotFound` döner; servis gövdeyi geçirir ve
// yalnız bu anahtarın toast'ını susturur (kvkkService.js). Bu durumda talep açılamaz:
// tablo ve "Yeni Talep" yerine açıklama gösterilir. Başka her okuma hatası "yüklenemedi".

const Container = styled.div``;

const EmptyState = styled.div`
  text-align: center;
  padding: 60px 20px;
  font-family: 'Nunito Sans', sans-serif;
  font-size: 14px;
  color: #A0A8B4;
`;

const NoteCell = styled.span`
  display: inline-block;
  max-width: 280px;
  overflow-wrap: anywhere;
`;

const TYPES = ['deletion', 'correction', 'access'];
const EMPLOYEE_NOT_FOUND = 'kvkk.request.employeeNotFound';
const NOTE_PREVIEW = 60;
// KvkkDataRequestRules ile aynı açıklama sınırları (Trim sonrası). İstemci ön-denetimi
// yalnız kolaylıktır; otorite backend 400'üdür.
const DESCRIPTION_MIN = 10;
const DESCRIPTION_MAX = 2000;

// Not hücresi kısa önizleme: ilk satırın ilk NOTE_PREVIEW karakteri. Tam metin çekmecede.
export const notePreview = (note) => {
  if (!note) return '-';
  const firstLine = String(note).split(/\r?\n/)[0];
  if (firstLine.length <= NOTE_PREVIEW && firstLine.length === note.length) return note;
  return `${firstLine.slice(0, NOTE_PREVIEW).trimEnd()}…`;
};

export default function MyKvkkRequestsPage() {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  const [rows, setRows] = useState([]);
  const [state, setState] = useState('loading');
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [selected, setSelected] = useState(null);

  const load = useCallback(() => {
    setState((prev) => (prev === 'ready' ? prev : 'loading'));
    getMyKvkkRequests().then((res) => {
      const body = res?.data;
      if (body?.status) {
        setRows(Array.isArray(body.data) ? body.data : []);
        setState('ready');
        return;
      }
      setRows([]);
      setState(body?.message === EMPLOYEE_NOT_FOUND ? 'noEmployee' : 'unavailable');
    });
  }, []);

  useEffect(() => { load(); }, [load]);

  const typeOptions = useMemo(() => TYPES.map((id) => ({ id, name: t(`kvkkRequest.type.${id}`) })), [t]);

  const objectSchema = useMemo(() => [
    {
      name: 'type', label: t('kvkkRequest.type.label'), type: 'select', grid: 12, required: true,
      options: typeOptions, idKey: 'id', labelKey: 'name', defaultValue: 'deletion',
    },
    {
      name: 'description', label: t('kvkkRequest.description'), type: 'textarea', grid: 12, required: true, rows: 6,
      minLength: DESCRIPTION_MIN, maxLength: DESCRIPTION_MAX,
      description: t('kvkkRequest.descriptionHint'),
    },
  ], [t, typeOptions]);

  // Uzunluk (10–2000) çekmecede ön-denetlenir (minLength/maxLength); otorite yine backend
  // (uzunluk, tür, aynı türde açık talep). 400/409 mesajını
  // interceptor toast'lar. Bileşen ikinci toast basmaz ve çekmeceyi AÇIK bırakır: kullanıcı
  // yazdığı açıklamayı kaybetmeden düzeltip yeniden gönderebilir.
  const handleSave = useCallback(async (formData) => {
    try {
      const res = await createMyKvkkRequest({ type: formData.type, description: formData.description });
      if (!res?.data?.status) return false;
      setDrawerOpen(false);
      load();
      return true;
    } catch {
      return false;
    }
  }, [load]);

  const columns = useMemo(() => [
    { name: 'id', label: 'ID', options: { filter: false, sort: false, display: 'excluded', download: false, print: false } },
    {
      name: 'type',
      label: t('kvkkRequest.type.label'),
      options: { filter: false, sort: true, customBodyRender: (v) => t(`kvkkRequest.type.${v}`) },
    },
    {
      name: 'status',
      label: t('kvkkRequest.status.label'),
      options: {
        filter: false,
        sort: true,
        customBodyRender: (v) => <StatusBadge $status={v}>{t(`kvkkRequest.status.${v}`)}</StatusBadge>,
      },
    },
    dateFilterColumn('createDate', t('kvkkRequest.createdAt'), lang, { clientSide: true, includeTime: true }),
    {
      name: 'closedAt',
      label: t('kvkkRequest.closedAt'),
      options: { filter: false, sort: true, customBodyRender: (v) => (v ? formatDateTime(v, lang) : '-') },
    },
    {
      name: 'hrNote',
      label: t('kvkkRequest.hrNote'),
      options: { filter: false, sort: false, customBodyRender: (v) => <NoteCell>{notePreview(v)}</NoteCell> },
    },
  ], [t, lang]);

  if (state === 'noEmployee') return <Container><EmptyState>{t('kvkkRequest.noEmployee')}</EmptyState></Container>;
  if (state === 'unavailable') return <Container><EmptyState>{t('kvkkRequest.unavailable')}</EmptyState></Container>;

  return (
    <Container>
      <DataTable
        title={t('kvkkRequest.myTitle')}
        serverSide={false}
        fetching={state === 'loading'}
        columns={columns}
        data={rows}
        pageSize={10}
        canCreateNewRecord
        newButtonLabel={t('kvkkRequest.new')}
        onNewButtonClick={() => setDrawerOpen(true)}
        onRowClick={setSelected}
      />

      <GenericFormDrawer
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        title={t('kvkkRequest.newTitle')}
        schema={objectSchema}
        initialValues={null}
        onSave={handleSave}
      />

      <KvkkRequestDetailDrawer request={selected} onClose={() => setSelected(null)} />
    </Container>
  );
}
